import { useEffect, useState } from "react";
import { Mail, Clock } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";
import { HELP_SECTIONS } from "@/components/help/helpSections";

const SECTIONS = HELP_SECTIONS;



export default function Help() {
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <PublicPageLayout>
      <SEO
        title="Help Center | Sorvaine"
        description="Get help with Sorvaine — the bid and tender management platform. Guides for getting started, the Knowledge Vault, AI behaviour, billing, troubleshooting, and security."
        canonical="https://sorvaine.com/help"
      />

      {/* Hero */}
      <section className="pt-32 pb-12 relative">
        <div className="max-w-[1200px] mx-auto px-6 md:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <div className="w-10 h-px bg-gradient-to-r from-primary to-transparent" />
            </div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium">Help Center</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] mb-6">
            How can we help?
          </h1>
          <p className="text-muted-foreground text-lg max-w-[60ch] leading-relaxed">
            Answers to the most common questions about Sorvaine. Can't find what you need?{" "}
            <a href="mailto:support@sorvaine.com" className="text-primary hover:underline">
              Email support
            </a>{" "}
            — we reply within 1 business day.
          </p>
        </div>
      </section>

      <div className="divider-glow" />

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Sticky nav */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 space-y-1">
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium mb-4">
                  Topics
                </p>
                {SECTIONS.map((s) => {
                  const Icon = s.icon;
                  const isActive = activeId === s.id;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {s.label}
                    </a>
                  );
                })}

                <div className="mt-8 pt-6 border-t border-border/60 space-y-3">
                  <a
                    href="mailto:support@sorvaine.com"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="w-4 h-4 shrink-0" />
                    support@sorvaine.com
                  </a>
                  <div className="flex items-center gap-3 px-3 py-2 text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5 shrink-0" />
                    Replies within 1 business day
                  </div>
                </div>
              </div>
            </aside>

            {/* Sections */}
            <div className="lg:col-span-9 space-y-20">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <section key={s.id} id={s.id} className="scroll-mt-24">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <h2 className="font-serif text-2xl md:text-3xl">{s.label}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mb-8 max-w-[65ch]">{s.intro}</p>

                    <div className="space-y-4">
                      {s.items.map((item, i) => (
                        <details
                          key={i}
                          className="group bg-card rounded-[10px] shadow-md border border-border/40 overflow-hidden"
                        >
                          <summary className="flex items-center justify-between gap-4 px-6 py-4 cursor-pointer list-none hover:bg-muted/30 transition-colors">
                            <span className="text-sm font-medium text-foreground">{item.q}</span>
                            <span className="text-muted-foreground text-xl leading-none transition-transform group-open:rotate-45">
                              +
                            </span>
                          </summary>
                          <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">
                            {item.a}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* Contact CTA */}
              <section className="bg-card rounded-[10px] shadow-md border border-border/40 p-8 md:p-10 text-center">
                <h3 className="font-serif text-2xl md:text-3xl mb-3">Still need help?</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-[50ch] mx-auto">
                  Our team replies to every message within 1 business day. We support bid and proposal teams
                  worldwide, across every timezone.
                </p>
                <a
                  href="mailto:support@sorvaine.com"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  support@sorvaine.com
                </a>
              </section>
            </div>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
